import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BottomNav } from "@/components/BottomNav";
import { Loader } from "@/components/Loader";
import { ChevronLeft, ShieldAlert, Phone, MessageCircle, MapPin, RotateCcw, LifeBuoy, Stethoscope, BookMarked, Bus } from "lucide-react";

const HOLD_MS = 3000;

function pickEmergency(support) {
  const contacts = support?.contacts || [];
  const phoneC = contacts.find((c) => c.phone) || {};
  const waC = contacts.find((c) => c.whatsapp) || {};
  return { phone: phoneC.phone || waC.whatsapp || "", whatsapp: waC.whatsapp || phoneC.phone || "" };
}

export const SosPage = () => {
  const { content, loading } = useContent();
  const { t, L } = useLang();
  const navigate = useNavigate();
  const [holding, setHolding] = useState(false);
  const [armed, setArmed] = useState(false);
  const timer = useRef(null);

  if (loading || !content) return <Loader />;

  const emergency = pickEmergency(content.support);

  const start = () => {
    if (armed) return;
    setHolding(true);
    timer.current = setTimeout(() => {
      setArmed(true);
      setHolding(false);
      if (navigator.vibrate) navigator.vibrate(60);
    }, HOLD_MS);
  };
  const cancel = () => {
    setHolding(false);
    if (timer.current) clearTimeout(timer.current);
  };
  const reset = () => {
    setArmed(false);
    setHolding(false);
  };

  const shareLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const { latitude, longitude } = pos.coords;
      const url = `https://maps.google.com/?q=${latitude},${longitude}`;
      if (navigator.share) navigator.share({ title: "My location", text: "I need help — my location", url }).catch(() => {});
      else window.open(url, "_blank");
    });
  };

  const guidance = [
    { icon: LifeBuoy, title: t("sos.lost"), body: t("sos.lostBody") },
    { icon: Stethoscope, title: t("sos.medical"), body: t("sos.medicalBody") },
    { icon: BookMarked, title: t("sos.passport"), body: t("sos.passportBody") },
    { icon: Bus, title: t("sos.transport"), body: t("sos.transportBody") },
  ];

  return (
    <div className="min-h-[100svh] bg-[#F7F4EE]" data-testid="sos-page">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/92 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-xl items-center justify-between px-3 py-2.5 sm:px-6">
          <button
            onClick={() => navigate("/more")}
            data-testid="back-button"
            className="inline-flex items-center gap-1 rounded-full py-1.5 pl-1.5 pr-3 text-sm font-semibold text-[#0A192F] transition-colors hover:bg-slate-100 active:scale-95"
          >
            <ChevronLeft size={20} strokeWidth={2.25} /> {t("back")}
          </button>
          <img src="/maqam-logo.png" alt="Maqam" className="h-6 w-auto" />
          <LanguageSwitcher variant="dark" />
        </div>
      </header>

      <div className="mx-auto w-full max-w-xl px-5 pb-28 pt-8 sm:px-6">
        <div className="mb-8 flex items-center gap-4 animate-fade-up">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white text-[#B42318] ring-2 ring-[#B42318]/25 shadow-sm">
            <ShieldAlert size={24} strokeWidth={1.9} />
          </span>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0A192F]">{t("sos.title")}</h1>
        </div>

        {/* Hold button / armed options */}
        {!armed ? (
          <div className="flex flex-col items-center py-4">
            <button
              onPointerDown={start}
              onPointerUp={cancel}
              onPointerLeave={cancel}
              onPointerCancel={cancel}
              data-testid="sos-hold-button"
              className="relative grid h-52 w-52 select-none place-items-center rounded-full bg-white shadow-[0_20px_50px_-16px_rgba(180,35,24,0.4)] ring-4 ring-[#B42318] transition-transform active:scale-95"
            >
              {/* red fill rises from the bottom while holding */}
              <span
                className="absolute inset-1.5 rounded-full bg-[#B42318]"
                style={{
                  clipPath: holding ? "inset(0 0 0 0)" : "inset(100% 0 0 0)",
                  transition: holding ? `clip-path ${HOLD_MS}ms linear` : "clip-path 250ms ease-out",
                }}
              />
              <div className="relative z-10 text-center">
                <ShieldAlert
                  size={40}
                  strokeWidth={1.7}
                  className={`mx-auto transition-colors duration-200 ${holding ? "text-white" : "text-[#B42318]"}`}
                />
                <span
                  className={`mt-2 block text-lg font-extrabold tracking-wide transition-colors duration-200 ${holding ? "text-white" : "text-[#0A192F]"}`}
                >
                  SOS
                </span>
              </div>
            </button>
            <p className="mt-6 text-sm font-medium text-slate-500">{holding ? t("sos.holding") : t("sos.hold")}</p>
          </div>
        ) : (
          <div className="space-y-3 animate-fade-up" data-testid="sos-options">
            <p className="mb-1 text-center text-sm font-semibold text-[#B42318]">{t("sos.ready")}</p>
            <a
              href={`tel:${emergency.phone}`}
              data-testid="sos-call"
              className="flex items-center gap-3 rounded-2xl bg-[#B42318] p-5 text-white shadow-lg transition-transform active:scale-[0.98]"
            >
              <Phone size={22} /> <span className="text-base font-bold">{t("sos.callEmergency")}</span>
            </a>
            <a
              href={`https://wa.me/${String(emergency.whatsapp).replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              data-testid="sos-whatsapp"
              className="flex items-center gap-3 rounded-2xl bg-[#128C7E] p-5 text-white shadow-lg transition-transform active:scale-[0.98]"
            >
              <MessageCircle size={22} /> <span className="text-base font-bold">{t("sos.whatsappEmergency")}</span>
            </a>
            <button
              onClick={shareLocation}
              data-testid="sos-location"
              className="flex w-full items-center gap-3 rounded-2xl bg-[#0A192F] p-5 text-left text-white shadow-lg transition-transform active:scale-[0.98]"
            >
              <MapPin size={22} /> <span className="text-base font-bold">{t("sos.shareLocation")}</span>
            </button>
            <button onClick={reset} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500">
              <RotateCcw size={14} /> {t("sos.reset")}
            </button>
          </div>
        )}

        {/* Guidance */}
        <h2 className="mb-4 mt-10 font-display text-xl font-bold text-[#0A192F]">{t("sos.guidance")}</h2>
        <div className="space-y-3">
          {guidance.map((g, i) => (
            <div key={i} className="maqam-card flex items-start gap-4 p-5">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#0A192F]/6 text-[#0A192F]">
                <g.icon size={20} strokeWidth={1.7} />
              </span>
              <div>
                <h3 className="font-display text-base font-bold text-[#0A192F]">{g.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{g.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default SosPage;
